import { useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import { findRoute } from '../services/routeFinder'
import { formatFare } from '../services/fareCalculator'
import { RouteResult, RoutePreference } from '../types/metro.types'
import { BannerAd, AD_SLOTS } from '../components/ads'

function RoutePlanner() {
  const { stations, lines, fares, graph, selectedCity, currentCity, isLoading } = useMetro()
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [sourceQuery, setSourceQuery] = useState('')
  const [destQuery, setDestQuery] = useState('')
  const [routePreference, setRoutePreference] = useState<RoutePreference>('shortest')
  const [routeResult, setRouteResult] = useState<RouteResult | null>(null)
  const [showSourceDropdown, setShowSourceDropdown] = useState(false)
  const [showDestDropdown, setShowDestDropdown] = useState(false)

  const sourceRef = useRef<HTMLDivElement>(null)
  const destRef = useRef<HTMLDivElement>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sourceRef.current && !sourceRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false)
      }
      if (destRef.current && !destRef.current.contains(event.target as Node)) {
        setShowDestDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredSourceStations = useMemo(() => {
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name))
    if (!sourceQuery.trim()) {
      return sorted
    }
    return sorted.filter(s => s.name.toLowerCase().includes(sourceQuery.toLowerCase()))
  }, [stations, sourceQuery])

  const filteredDestStations = useMemo(() => {
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name))
    if (!destQuery.trim()) {
      return sorted
    }
    return sorted.filter(s => s.name.toLowerCase().includes(destQuery.toLowerCase()))
  }, [stations, destQuery])

  const handleFindRoute = () => {
    if (!source || !destination || !graph || !fares) return

    const result = findRoute(
      graph,
      source,
      destination,
      stations,
      lines,
      fares,
      { preference: routePreference }
    )

    setRouteResult(result)
  }

  const swapStations = () => {
    setSource(destination)
    setDestination(source)
    setSourceQuery(destQuery)
    setDestQuery(sourceQuery)
    setRouteResult(null)
  }

  const getLineColor = (lineId: string) => {
    const line = lines.find(l => l.id === lineId)
    return line?.colorHex || '#4A90D9'
  }

  const getStationLineColors = (stationLines: string[]) => {
    return stationLines.map(lineId => {
      const line = lines.find(l => l.id === lineId)
      return line?.colorHex || '#999'
    })
  }

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">No City Selected</h2>
            <p className="text-neutral-dark/70 mb-4">Please select a city to plan routes</p>
            <Link to="/cities" className="btn-primary">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || (selectedCity && stations.length === 0)) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading metro data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive max-w-4xl">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-2">
          Route Planner
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          {currentCity?.metroName} - Find the best route between stations
        </p>

        {/* Search Form */}
        <div className="card mb-8">
          <div className="space-y-4">
            {/* Source Station */}
            <div className="relative" ref={sourceRef}>
              <label className="block text-sm font-medium mb-2">From Station</label>
              <input
                type="text"
                placeholder="Click to select or type to search..."
                value={sourceQuery}
                onChange={(e) => {
                  setSourceQuery(e.target.value)
                  setShowSourceDropdown(true)
                  setSource('')
                }}
                onFocus={() => setShowSourceDropdown(true)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              />
              {showSourceDropdown && filteredSourceStations.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {/* Header */}
                  <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b text-sm text-neutral-dark/70">
                    {sourceQuery ? `${filteredSourceStations.length} stations found` : `All ${stations.length} stations`}
                  </div>
                  {filteredSourceStations.map(station => {
                    const lineColors = getStationLineColors(station.lines)
                    return (
                      <button
                        key={station.id}
                        className="w-full text-left px-4 py-3 hover:bg-metro-blue-soft flex items-center gap-3 border-b border-gray-50"
                        onClick={() => {
                          setSource(station.id)
                          setSourceQuery(station.name)
                          setShowSourceDropdown(false)
                        }}
                      >
                        {/* Line color indicators */}
                        <div className="flex gap-0.5 flex-shrink-0">
                          {lineColors.slice(0, 3).map((color, i) => (
                            <div
                              key={i}
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">{station.name}</span>
                          {station.isInterchange && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-metro-violet-soft text-metro-violet rounded">
                              Interchange
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={swapStations}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Swap stations"
              >
                <svg className="w-6 h-6 text-neutral-dark/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </button>
            </div>

            {/* Destination Station */}
            <div className="relative" ref={destRef}>
              <label className="block text-sm font-medium mb-2">To Station</label>
              <input
                type="text"
                placeholder="Click to select or type to search..."
                value={destQuery}
                onChange={(e) => {
                  setDestQuery(e.target.value)
                  setShowDestDropdown(true)
                  setDestination('')
                }}
                onFocus={() => setShowDestDropdown(true)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              />
              {showDestDropdown && filteredDestStations.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {/* Header */}
                  <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b text-sm text-neutral-dark/70">
                    {destQuery ? `${filteredDestStations.length} stations found` : `All ${stations.length} stations`}
                  </div>
                  {filteredDestStations.map(station => {
                    const lineColors = getStationLineColors(station.lines)
                    return (
                      <button
                        key={station.id}
                        className="w-full text-left px-4 py-3 hover:bg-metro-blue-soft flex items-center gap-3 border-b border-gray-50"
                        onClick={() => {
                          setDestination(station.id)
                          setDestQuery(station.name)
                          setShowDestDropdown(false)
                        }}
                      >
                        {/* Line color indicators */}
                        <div className="flex gap-0.5 flex-shrink-0">
                          {lineColors.slice(0, 3).map((color, i) => (
                            <div
                              key={i}
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">{station.name}</span>
                          {station.isInterchange && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-metro-violet-soft text-metro-violet rounded">
                              Interchange
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <button
              onClick={handleFindRoute}
              disabled={!source || !destination}
              className="w-full btn-primary mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Find Route
            </button>
          </div>
        </div>

        {/* Route Options */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setRoutePreference('shortest')}
            className={`flex-1 card text-center transition-colors ${
              routePreference === 'shortest' ? 'border-2 border-metro-blue bg-metro-blue-soft' : ''
            }`}
          >
            <span className="block text-2xl mb-1">🚀</span>
            <span className="font-medium">Shortest Route</span>
          </button>
          <button
            onClick={() => setRoutePreference('minInterchange')}
            className={`flex-1 card text-center transition-colors ${
              routePreference === 'minInterchange' ? 'border-2 border-metro-blue bg-metro-blue-soft' : ''
            }`}
          >
            <span className="block text-2xl mb-1">🔄</span>
            <span className="font-medium">Min Interchange</span>
          </button>
        </div>

        {/* Route Result */}
        {routeResult ? (
          <div className="card">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b">
              <div className="text-center">
                <div className="text-2xl font-bold text-metro-blue">{routeResult.totalStations}</div>
                <div className="text-sm text-neutral-dark/70">Stations</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-metro-violet">{routeResult.totalInterchanges}</div>
                <div className="text-sm text-neutral-dark/70">Interchanges</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-metro-green">{routeResult.estimatedTimeMinutes} min</div>
                <div className="text-sm text-neutral-dark/70">Duration</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-metro-orange">{formatFare(routeResult.fare.tokenFare)}</div>
                <div className="text-sm text-neutral-dark/70">Fare</div>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {routeResult.steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-3 rounded-lg ${
                    step.action === 'interchange' ? 'bg-metro-yellow-soft' :
                    step.action === 'board' ? 'bg-metro-green-soft' :
                    step.action === 'exit' ? 'bg-metro-red-soft' : ''
                  }`}
                >
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: step.line?.colorHex || getLineColor(step.line?.id || '') }}
                  ></div>
                  <div className="flex-1">
                    <div className="font-medium">{step.station?.name || 'Unknown Station'}</div>
                    <div className="text-sm text-neutral-dark/70">
                      {step.action === 'board' && (
                        <>
                          Board {step.line?.name || 'Metro'} → {step.towards || 'Destination'}
                          {step.platform && <span className="ml-2 text-metro-blue font-medium">• Platform {step.platform}</span>}
                        </>
                      )}
                      {step.action === 'interchange' && (
                        <>
                          Change to {step.direction || 'next line'} → {step.towards || 'Destination'}
                          {step.platform && <span className="ml-2 text-metro-violet font-medium">• Platform {step.platform}</span>}
                        </>
                      )}
                      {step.action === 'exit' && 'Exit here'}
                      {step.action === 'travel' && (step.line?.name || 'Metro Line')}
                    </div>
                  </div>
                  {step.cumulativeTimeMinutes > 0 && (
                    <div className="text-sm text-neutral-dark/50">
                      {step.cumulativeTimeMinutes} min
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Fare Details */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-neutral-dark/70">Token Fare: </span>
                  <span className="font-semibold">{formatFare(routeResult.fare.tokenFare)}</span>
                </div>
                <div>
                  <span className="text-neutral-dark/70">Smart Card: </span>
                  <span className="font-semibold text-metro-green">{formatFare(routeResult.fare.smartCardFare)}</span>
                  {routeResult.fare.savings > 0 && (
                    <span className="ml-2 text-sm text-metro-green">
                      (Save {formatFare(routeResult.fare.savings)})
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card text-center py-12 text-neutral-dark/50">
            <span className="text-5xl mb-4 block">🚇</span>
            <p>Enter source and destination to find the best route</p>
          </div>
        )}

        {/* Banner Ad - After Route Results */}
        {routeResult && (
          <BannerAd
            slot={AD_SLOTS.ROUTE_RESULTS}
            format="horizontal"
            wrapperClassName="mt-8 flex justify-center"
          />
        )}
      </div>
    </div>
  )
}

export default RoutePlanner
